import { type NotificationModel } from '@/shared/api'
import { showDeleteConfirmationModal } from '@/shared/lib/antd-utils'
import { formatDateTimeStringToDateString } from '@/shared/lib/form-utils'
import { COMMON_COLUMN_WIDTH } from '@/shared/lib/styling'
import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  FieldTimeOutlined,
} from '@ant-design/icons'
import { Badge, Button, Space, type TableProps } from 'antd'
import { useUnit } from 'effector-react'

import {
  NotificationConditionTypeEnum,
  type NotificationsSortState,
  getNotificationConditionTypeString,
} from '../../lib'
import { COLUMN_WIDTH } from '../../lib/column-width'
import {
  NotificationStatusTypeEnum,
  getNotificationStatusTypeColorString,
  getNotificationStatusTypeString,
} from '../../lib/notification-status'
import { notificationDeleted } from '../../model'

interface useNotificationsTableColumnsProps {
  onEdit: (NotificationId: string) => void
  onView: (NotificationId: string) => void
  sort: NotificationsSortState
}
export const useNotificationsTableColumns = ({
  onView: handleView,
  onEdit: handleEdit,
  sort,
}: useNotificationsTableColumnsProps): TableProps<NotificationModel>['columns'] => {
  const deleteNotification = useUnit(notificationDeleted)
  const handleDelete = (id: string) => {
    showDeleteConfirmationModal({
      onConfirm: () => {
        deleteNotification({ id })
      },
    })
  }

  return [
    {
      title: 'Наименование',
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      ellipsis: true,
      sortOrder:
        sort.field === 'name'
          ? sort.order === 'asc'
            ? 'ascend'
            : sort.order === 'desc'
              ? 'descend'
              : null
          : null,
      render: (_, notification) => notification.name,
    },
    {
      title: 'Статус',
      dataIndex: 'deliveryStatus',
      width: COLUMN_WIDTH.DELIVERY_STATUS,
      key: 'deliveryStatus',
      sorter: true,
      sortOrder:
        sort.field === 'deliveryStatus'
          ? sort.order === 'asc'
            ? 'ascend'
            : sort.order === 'desc'
              ? 'descend'
              : null
          : null,
      render: (_, notification) => (
        <Badge
          color={getNotificationStatusTypeColorString(
            notification.deliveryStatus.status,
          )}
          text={
            <>
              {getNotificationStatusTypeString(
                notification.deliveryStatus.status,
              )}
              {notification.deliveryStatus.status ===
                NotificationStatusTypeEnum.Sent && (
                <>
                  {' '}
                  ({`${notification.deliveryStatus.deliveryCount}/27`}){' '}
                  <FieldTimeOutlined />
                </>
              )}
            </>
          }
        />
      ),
    },
    {
      title: 'Дата последней отправки',
      dataIndex: 'sendOnDate',
      width: COLUMN_WIDTH.SEND_ON_DATE,
      key: 'sendOnDate',
      sorter: true,
      sortOrder:
        sort.field === 'sendOnDate'
          ? sort.order === 'asc'
            ? 'ascend'
            : sort.order === 'desc'
              ? 'descend'
              : null
          : null,
      render: (_, notification) =>
        notification.sendOnDate
          ? formatDateTimeStringToDateString(notification.sendOnDate, false)
          : notification.conditionType === NotificationConditionTypeEnum.Instant
            ? getNotificationConditionTypeString(notification.conditionType)
            : '',
    },
    {
      title: '',
      key: 'actions',
      width: COMMON_COLUMN_WIDTH.ACTIONS,
      align: 'center',
      render: (_, notification) => (
        <Space size="middle">
          {notification.deliveryStatus.status ===
            NotificationStatusTypeEnum.Planned &&
          notification.conditionType !==
            NotificationConditionTypeEnum.Instant ? (
            <Button
              onClick={() => handleEdit(notification.id)}
              icon={<EditOutlined />}
            />
          ) : (
            <Button
              onClick={() => handleView(notification.id)}
              icon={<EyeOutlined />}
            />
          )}
          <Button
            onClick={() => handleDelete(notification.id)}
            icon={<DeleteOutlined style={{ color: 'red' }} />}
          />
        </Space>
      ),
    },
  ]
}
